import axios from "axios";
import { API_BASE_URL } from "../utils/api";

// Imported lazily via getter to avoid circular dependency (store → axiosInstance → store)
let _store;
const getStore = () => {
    if (!_store) {
        _store = require("../lib/store").store;
    }
    return _store;
};

// Guard flag — prevents multiple concurrent 401s from each triggering a redirect.
// On production, several requests fire in parallel and all return 401 simultaneously.
// Without this, each one calls window.location.href causing a re-mount loop.
let isRedirecting = false;

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
    },
});

// Response interceptor for consistent error handling
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        // Skip interceptor for canceled requests
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const currentPath = window.location.pathname;
        const isSignInPage = currentPath.includes("/sign-in") || currentPath.includes("/login");

        // Standard error handling
        if (error.response?.status === 401) {
            // If we're already redirecting (another concurrent 401 beat us here),
            // just silently reject — don't fire another redirect or state clear.
            if (isRedirecting || isSignInPage) {
                return Promise.reject(error);
            }

            isRedirecting = true;

            // Clear Redux in-memory state immediately so components stop firing API calls
            try {
                const { logout } = require("../features/auth/authSlice");
                getStore().dispatch(logout());
            } catch (e) {
                // silently fail if store isn't available
            }

            // Clear persisted storage
            localStorage.removeItem("user");
            localStorage.removeItem("isLoggedIn");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("persist:root");

            window.location.href = "/sign-in";
            return Promise.reject(error);
        }

        // Standard maintenance redirect
        if ([502, 503, 504].includes(error.response?.status)) {
            const isMaintenancePage = window.location.pathname.includes("/maintenance");
            if (!isMaintenancePage) {
                window.location.href = "/maintenance";
                return Promise.reject(error);
            }
        }

        // Standardize error message extraction
        const message =
            error.response?.data?.message ||
            error.response?.data?.data?.message ||
            error.response?.data?.error ||
            error.response?.data?._dev?.technicalMessage ||
            error.message ||
            "An unexpected error occurred";

        // Create a new error object with the standardized message
        const customError = new Error(message);
        customError.status = error.response?.status;
        customError.originalError = error;
        customError.data = error.response?.data; // Preserve data for further inspect if needed

        return Promise.reject(customError);
    }
);

export default axiosInstance;
